import type { SongEntry } from './SongEntry.js';

/**
 * Storage for songs, as the library needs it.
 *
 * An interface for the same reason `ChartGenerationClient` is one: the browser
 * build stores in IndexedDB, and a desktop shell would rather write files. What
 * matters is that everything above this line asks for "the music of song X" and
 * never for "object store `assets`, key `[X, music]`".
 *
 * ## Why assets and not archives
 *
 * The obvious alternative is to keep each Song Pack as its stored `.zip` and
 * inflate on demand. It was rejected for three specific reasons, all of which
 * are about the music video:
 *
 *   - **PLAY** needs `music.wav` and one chart. Getting them out of an archive
 *     that also contains a 200 MB video means paying for the whole container.
 *   - **Mirror Drift** needs a `Blob` to hand to `createObjectURL`. Stored as an
 *     asset it already is one, and the video never enters JavaScript memory at
 *     all. Stored in an archive it must be inflated into a `Uint8Array` first.
 *   - **Storage** of an MP4 inside a ZIP saves nothing; the bytes are already
 *     compressed, and deflating them again costs CPU to grow the file slightly.
 *
 * What archives would have won is EXPORT, which becomes a rebuild rather than a
 * copy. That is paid for by storing every asset **verbatim** — the manifest and
 * chart bytes are the producer's own, not re-serialised — so a rebuilt archive
 * carries content identical to the one that came in.
 */

/** Logical asset names. The entry name each maps to is a separate question. */
export const ASSET_NAMES = [
  'manifest',
  'easy',
  'normal',
  'hard',
  'music',
  'cover',
  'video',
] as const;

export type AssetName = (typeof ASSET_NAMES)[number];

/** Assets every song must have. */
export const REQUIRED_ASSETS: readonly AssetName[] = ['manifest', 'easy', 'normal', 'hard', 'music'];

/** Fixed archive entry names for the assets version 1 fixes. */
const FIXED_ENTRY_NAMES: Readonly<Partial<Record<AssetName, string>>> = Object.freeze({
  manifest: 'manifest.json',
  easy: 'easy.chart',
  normal: 'normal.chart',
  hard: 'hard.chart',
  music: 'music.wav',
  cover: 'cover.png',
});

/**
 * The archive entry an asset is written to.
 *
 * Everything but the video is fixed by version 1. The video's name comes from
 * the manifest, because it carries a real extension and `video.webm` is as valid
 * as `video.mp4`.
 */
export function entryNameFor(name: AssetName, entry: SongEntry): string {
  const fixed = FIXED_ENTRY_NAMES[name];
  if (fixed !== undefined) return fixed;
  return entry.mvReference ?? 'video.mp4';
}

export interface SongRepository {
  /** Every index record. Read once at launch; no assets are touched. */
  list(): Promise<SongEntry[]>;
  get(packId: string): Promise<SongEntry | null>;
  /**
   * Writes an index record and its assets as one unit.
   *
   * Must be atomic: a failure part-way leaves no entry and no orphan assets.
   * Any asset already stored for this `packId` and not supplied here is removed,
   * so replacing a song cannot leave the previous one's video behind.
   */
  put(entry: SongEntry, assets: ReadonlyMap<AssetName, Blob>): Promise<void>;
  /** One asset, or null when the song does not carry it. */
  asset(packId: string, name: AssetName): Promise<Blob | null>;
  /** Several assets in one transaction. Absent ones are simply not in the map. */
  assets(packId: string, names: readonly AssetName[]): Promise<Map<AssetName, Blob>>;
  /** Removes the index record and every asset. Returns whether anything went. */
  remove(packId: string): Promise<boolean>;
  close(): void;
}

/** Bytes each asset occupies, for the index record. */
export function measure(assets: ReadonlyMap<AssetName, Blob>): Record<string, number> {
  const bytes: Record<string, number> = {};
  for (const [name, blob] of assets) bytes[name] = blob.size;
  return bytes;
}
